import type { Annotation } from "building-types/annotation/annotationTypes";
import type { Doc } from "building-types/doc";
import type { Table } from "common/data/table";
import objectKeyFilter from "common/utils/objectKeyFilter";
import { DocEntityNodeRegistry } from "doc/entity/docEntityNodeRegistry";
import { docAnnotationOrDrawingIsModelSpaceHosted } from "doc/entity/docEntityNodes/ContentTemplateDocEntityNode";

function docAnnotationIsContentTemplateHosted(doc: Doc, annot: Annotation) {
  return (
    doc.contentTemplates[annot.parentId ?? ""] !== undefined ||
    doc.contentTemplateInstances[annot.parentId ?? ""] !== undefined
  );
}

const modelSpaceAnnotationCache = new WeakMap<Doc, Table<Annotation>>();

/**
 * Selects model space annotation from the doc. The annotation is returned in a table that is
 * sorted by sort index.
 */
export function selectModelSpaceAnnotation(doc: Doc): Table<Annotation> {
  const cached = modelSpaceAnnotationCache.get(doc);
  if (cached) {
    return cached;
  }

  // This needs to detect annotations that are directly hosted in model space,
  // or that are hosted on drawing templates that have model space hosted instances.
  const result = objectKeyFilter(doc.annotation, (_, annot) =>
    docAnnotationOrDrawingIsModelSpaceHosted(doc, annot.parentId, DocEntityNodeRegistry.getSource()),
  );
  modelSpaceAnnotationCache.set(doc, result);
  return result;
}

/**
 * Selects annotation that's hosted on any type of content templates. Note that this is not the reverse of "selectModelSpaceAnnotation",
 * since the same content template can have both model space instances and viewport references / sheet space instances.
 */
export function selectContentTemplateHostedAnnotation(doc: Doc): Table<Annotation> {
  return objectKeyFilter(doc.annotation, (_, annot) => docAnnotationIsContentTemplateHosted(doc, annot));
}
